/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";

import {EmGroup} from '../../models/em-group';
import {GroupType} from '../../models/group-type';
import {InstanceData} from "../../models/instance-data";
import {Service} from "../../models/service";
import {ServiceInstance} from "../../models/service-instance";

import {GroupService} from '../../services/group.service';
import {ServiceService} from "../../services/service.service";

import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import {INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE} from "../../app-constants";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { SimpleIndefiniteProgressDialogComponent } from '../simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component';
import { FilterOption, FilterType, REMOVE_FILTER } from '../../models/filter-option';

@Component({
  templateUrl: './services.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class ServicesComponent implements OnInit {

    /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
    @ViewChild(SimpleIndefiniteProgressDialogComponent) 
    indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mServices: ServiceUI[] = [];
  mServiceInstances: ServiceInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Services, 2 = Service Instances


  //Entity Type Dialog Variables
  mEntity: Service;
  mEntityName: string;
  mEntityType = GroupType.SERVICE;
  mEntityTypeName = "Service";

  // All the Filter Options available
  filterOptionForServices: FilterOption[] = [];
  filterOptionForServiceInstances: FilterOption[] = [];

  // The Filter Option Selected by the user
  selectedFilterOptionForServices: FilterOption = REMOVE_FILTER;
  selectedFilterOptionForServiceInstances: FilterOption = REMOVE_FILTER;
  /** Active filters for the Service Instances (Name + Type), AND'd together */
  activeFilterOptionsForServiceInstances: FilterOption[] = [];


  //Entity Instance Dialog Variables
  mInstance: ServiceInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: Service;
  mInstanceType = GroupType.SERVICEINSTANCE;
  mInstanceTypeName = "Service";
  mAvailableEntityTypes: Service[];

  currentPageForServices = 1;
  currentPageForServiceInstances = 1;

  totalNumberOfServices = 0;
  totalNumberOfServiceInstances = 0;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  constructor(
    private mRoute: ActivatedRoute,
    private mServiceService: ServiceService,
    private mGroupService: GroupService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) {}



  ngOnInit(): void {
    this.refreshFilterOptionsForServices();
    this.refreshFilterOptionsForServiceInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];
    this.mRoute.data.subscribe((data: 
      { 
        services: PagedResults<Service>, 
        serviceInstances: PagedResults<ServiceInstance>}
      ) => {
        this.updatePagedServices(data.services);
        this.updatePagedServiceInstances(data.serviceInstances)
  });
}



  isSingleServiceSelected(): boolean {
    if (this.mServices != null && this.mServices.length > 0) {
      let oneSelected = false;
      for (const service of this.mServices) {
        if (service.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyServicesSelected(): boolean {
    return this.mServices.some(service => service.mSelected);
  }

  isAllServicesSelected(): boolean {
    return this.mServices !== null && this.mServices.every(service => service.mSelected);
  }

  setAllServicesSelected(event: any): void {
    this.mServices.forEach(service => service.mSelected = event.target.checked);
  }





  isSingleServiceInstanceSelected(): boolean {
    if (this.mServiceInstances != null && this.mServiceInstances.length > 0) {
      let oneSelected = false;
      for (const serviceInstance of this.mServiceInstances) {
        if (serviceInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyServiceInstancesSelected(): boolean {
    return this.mServiceInstances.some(serviceInstance => serviceInstance.mSelected);
  }

  isAllServiceInstancesSelected(): boolean {
    return this.mServiceInstances !== null && this.mServiceInstances.every(serviceInstance => serviceInstance.mSelected);
  }

  setAllServiceInstancesSelected(event: any): void {
    this.mServiceInstances.forEach(serviceInstance => serviceInstance.mSelected = event.target.checked);
  }





  getSelectedServices(): Service[] {
    const services: Service[] = [];
    this.mServices.filter(ui => ui.mSelected).forEach(ui => services.push(ui.mService));
    return services;
  }

  getSelectedServiceIds(): number[] {
    const ids: number[] = [];
    this.mServices.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mService.id));
    return ids;
  }

  getSelectedServiceNames(): string[] {
    const names: string[] = [];
    this.mServices.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mService.name));
    return names;
  }





  getSelectedServiceInstances(): ServiceInstance[] {
    const serviceInstances: ServiceInstance[] = [];
    this.mServiceInstances.filter(ui => ui.mSelected).forEach(ui => serviceInstances.push(ui.mServiceInstance));
    return serviceInstances;
  }

  getSelectedServiceInstanceIds(): number[] {
    const ids: number[] = [];
    this.mServiceInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mServiceInstance.id));
    return ids;
  }

  getSelectedServiceInstanceNames(): string[] {
    const names: string[] = [];
    this.mServiceInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mServiceInstance.name));
    return names;
  }





  manageServiceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.SERVICE);
  }

  manageServiceInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.SERVICEINSTANCE);
  }





  addSelectedServicesToGroupClicked(): void {
    if (this.isAnyServicesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.SERVICE, this.getSelectedServiceNames());
    }
  }

  addSelectedServiceInstancesToGroupClicked(): void {
    if (this.isAnyServiceInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.SERVICEINSTANCE, this.getSelectedServiceInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.SERVICE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedServiceIds())
          .then(
            success => {
              this.refreshServices();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.SERVICEINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedServiceInstanceIds())
          .then(
            success => {
              this.refreshServiceInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  removeSelectedServicesFromGroupClicked(): void {
    if (this.isAnyServicesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const serviceNames: string[] = [];
      this.getSelectedServices().forEach(service => {
        serviceNames.push(service.name);
        if (service.groups != null) {
          service.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, serviceNames);
    }
  }

  removeSelectedServiceInstancesFromGroupClicked(): void {
    if (this.isAnyServiceInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const serviceInstanceNames: string[] = [];
      this.getSelectedServiceInstances().forEach(serviceInstance => {
        serviceInstanceNames.push(serviceInstance.name);
        if (serviceInstance.groups != null) {
          serviceInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, serviceInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.SERVICE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedServiceIds())
          .then(
            success => {
              this.refreshServices();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.SERVICEINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedServiceInstanceIds())
          .then(
            success => {
              this.refreshServiceInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.SERVICE: {
        this
          .mGroupService
          .getAllServicesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.SERVICEINSTANCE: {
        this
          .mGroupService
          .getAllServiceInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  addServiceClicked(): void {
    this.mEntity = new Service();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedServiceClicked(): void {
    if (this.isSingleServiceSelected()) {
      this.editService(this.getSelectedServices()[0]);
    }
  }

  duplicateSelectedServiceClicked(): void {
    if (this.isSingleServiceSelected()) {
      this
        .mServiceService
        .getServiceById(this.getSelectedServices()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedServicesClicked(): void {
    if (this.isAnyServicesSelected()) {
      this.mDeleteFlag = 1; //1 = Services
      this.mConfirmDeleteDialog.show("service type", this.getSelectedServiceNames());
    }
  }

  editService(service: Service): void {
    this
      .mServiceService
      .getServiceById(service.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.entityParent = response.parent;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
              this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Service... Create it
      this
        .mServiceService
        .createService(this.mEntity)
        .then(
          response => {
            this.refreshServices();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Service... Update it
      this
        .mServiceService
        .updateService(this.mEntity)
        .then(
          response => {
            this.refreshServices();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }





  addServiceInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ServiceInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataAndShowDialog();
  }

  editSelectedServiceInstanceClicked(): void {
    if (this.isSingleServiceInstanceSelected()) {
      this.editServiceInstance(this.getSelectedServiceInstances()[0]);
    }
  }

  duplicateSelectedServiceInstanceClicked(): void {
    if (this.isSingleServiceInstanceSelected()) {
      this
      .mServiceService
      .getServiceInstanceById(this.getSelectedServiceInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedServiceInstancesClicked(): void {
    if (this.isAnyServiceInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Service Instances
      this.mConfirmDeleteDialog.show("service", this.getSelectedServiceInstanceNames());
    }
  }

  editServiceInstance(serviceInstance: ServiceInstance): void {
    this
      .mServiceService
      .getServiceInstanceById(serviceInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataAndShowDialog(): Promise<void> {
    //Create a List of the available Services that the Instance can be a type of from the List already obtained
    this.mAvailableEntityTypes = await this.mServiceService.getAllServices();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.service == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.service.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.service.functions;
      this.mInstanceEntityType.properties = this.mInstance.service.properties;
    }

    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Show the Entity Instance Dialog
    this.mEntityInstanceDialogComponent.showDialog();
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Service Instance to be created
    this.mInstance.service = this.mInstanceEntityType;

    if (this.mInstance.id == null) {
      //ID is null, Service Instance does not exist!  Create it...
      this
        .mServiceService
        .createServiceInstance(this.mInstance)
        .then(
          response => {
            this.refreshServiceInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Service Instance exists!  Update it...
      this
        .mServiceService
        .updateServiceInstance(this.mInstance)
        .then(
          response => {
            this.refreshServiceInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: Service): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mServiceService
        .getServiceById(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  refreshInstanceData(): void {
    this
      .mServiceService
      .getServiceInstanceById(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }





  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Services
      this
        .mServiceService
        .deleteServices(this.getSelectedServiceIds())
        .then(
          response => {
            this.refreshServices();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Service Instances
      this
        .mServiceService
        .deleteServiceInstances(this.getSelectedServiceInstanceIds())
        .then(
          response => {
            this.refreshServiceInstances();
          },
          error => this.handleError(error)
        );
    }
  }

    /**
   * Called when the "Reset Driver" List Item is clicked in the "OPTIONS" drop down.
   */
  resetDriverClicked(): void {
    if (this.isSingleServiceInstanceSelected()) {
      this.indefiniteProgressDialog.show("Performing Action", "Reseting Driver...");
      this._resetDriver();
    }
  }

  /**
   * Does the underlying logic for resetting a Service's Driver
   */
  private async _resetDriver(): Promise<void> {
    const serviceId = this.getSelectedServiceInstanceIds()[0];
    console.log(`Reseting Service for ID: ${serviceId}`);

    try {
      await this.mServiceService.resetServiceDriver(serviceId);
    } catch (error) {
     this.indefiniteProgressDialog.hide()
      this.handleError(error);
      return;
    }

    this.refreshServiceInstances();
    this.indefiniteProgressDialog.hide()
  }

      /******************** FILTER FUNCTIONS FOR Server OBJECTS *****************/

      private refreshFilterOptionsForServices(): void {
        this.filterOptionForServices = [
          REMOVE_FILTER,
          new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
        ];
      }

      // Called by <app-filter-bar> whenever the active filter or its value changes.
      onServicesFilterChanged(option: FilterOption): void {
        this.selectedFilterOptionForServices = option;
        this.currentPageForServices = 1;
        this.refreshServices();
      }
    
      filterStringChangedServerObjects(pageNumber?: number): void {
        if (!pageNumber) { pageNumber = 1; }
        // Show only size on this new page
        console.log("page Changed to " + pageNumber);
        this.currentPageForServices = pageNumber;
    
        this.pageChangedServices(pageNumber);
    
      }
    
    
        /******************** FILTER FUNCTIONS FOR Server OBJECTS INSTANCES *****************/
    
      private refreshFilterOptionsForServiceInstances(): void {
        this.filterOptionForServiceInstances = [
          REMOVE_FILTER,
          new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
          new FilterOption({ column: "service.name", label: "Type", type: FilterType.STRING }),
        ];
      }

      // Called by <app-filter-bar> whenever the active filter or its value changes.
      onServiceInstancesFilterChanged(option: FilterOption): void {
        this.selectedFilterOptionForServiceInstances = option;
        this.currentPageForServiceInstances = 1;
        this.refreshServiceInstances();
      }
    
      filterStringChangedServiceInstances(pageNumber?: number): void {
        if (!pageNumber) { pageNumber = 1; }
        // Show only size on this new page
        console.log("page Changed to " + pageNumber);
        this.currentPageForServiceInstances = pageNumber;
    
        this.pageChangedServiceInstances(pageNumber);
    
      }
    
      handleError(error: any): void {
        /* New handling (TJB 04/18/2023) */
        if (error?.error != null) {
          this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
        } 
        
        /* Default back to old handling */
        else if (error != null) {
          const response: Response = error;
          alert(response.text());
        }
      }
    
    
      /******************** PAGINATOR FUNCTIONS FOR Server OBJECT  *****************/
    
      pageChangedServices(pageNumber: number): void {
        if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
          return;
        }
    
        if(this.currentPageForServices == pageNumber) {
          return;
        }
    
        this.currentPageForServices = pageNumber;
        this.refreshServices();
      }
    
    
      private async refreshServices(): Promise<void> {
        this.spinnerService.show();
        let pagedData: PagedResults<Service>;
        try {
          pagedData = await this.mServiceService.getAllServicesForPaging((this.currentPageForServices - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.selectedFilterOptionForServices.apiValue);
        } catch (error) {
          this.spinnerService.hide();
          this.handleError(error);
          return;
        }
    
        this.updatePagedServices(pagedData);
        this.spinnerService.hide();
      }
    
    
      private updatePagedServices(pagedData: PagedResults<Service>): void {
        const uiObjects: ServiceUI[] = [];
        if(pagedData.data != null) {
          pagedData.data.forEach(ServerObject => {
            const ui = new ServiceUI();
            ui.mService = ServerObject;
            ui.mSelected = false;
            uiObjects.push(ui);
          })
        }
        this.mServices = uiObjects;
    
        this.totalNumberOfServices = pagedData.size;
      }
    
    
      /******************** PAGINATOR FUNCTIONS FOR Server OBJECT INSTANCES *****************/
    
      pageChangedServiceInstances(pageNumber: number): void {
        if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
          return;
        }
    
        if(this.currentPageForServiceInstances == pageNumber) {
          return;
        }
    
        this.currentPageForServiceInstances = pageNumber;
        this.refreshServiceInstances();
      }
    
      private async refreshServiceInstances(): Promise<void> {
        this.spinnerService.show();
        let pagedData: PagedResults<ServiceInstance>;
        try {
          pagedData = await this.mServiceService.getAllServiceInstancesForPagingWithFilters((this.currentPageForServiceInstances - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.activeFilterOptionsForServiceInstances);
        } catch (error) {
          this.spinnerService.hide();
          this.handleError(error);
          return;
        }
        
        this.updatePagedServiceInstances(pagedData);
        this.spinnerService.hide();
      }
    
      private updatePagedServiceInstances(pagedData: PagedResults<ServiceInstance>): void {
        const uiObjects: ServiceInstanceUI[] = [];
        if(pagedData.data != null) {
          pagedData.data.forEach(ServerObjectInstances => {
            const ui = new ServiceInstanceUI();
            ui.mServiceInstance = ServerObjectInstances;
            ui.mSelected = false;
            uiObjects.push(ui);
          })
        }
    
        this.mServiceInstances = uiObjects;
        this.totalNumberOfServiceInstances = pagedData.size;
      }
    
       /******************** RESULTS PER PAGE FUNCTIONS  *****************/
    
      manageResultPerPageClickedForTypes(value: number): void{
        this.selectedPageSizeValueForTypes = value;
        this.currentPageForServices = 1;
        this.refreshServices();
      }
    
      manageResultPerPageClickedForInstances(value: number): void {
        this.selectedPageSizeValueForInstances = value;
        this.currentPageForServiceInstances = 1;
        this.refreshServiceInstances();
      }
    
  /*  
*  Handling the Right click event only on Tags Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, serviceInstance: ServiceInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Seervices Instance details using the ID
    this
      .mServiceService
      .getServiceInstanceById(serviceInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.service.functions) && (response.service.functions).length > 0) {
              this.functionList = response.service.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }
  

}

class ServiceUI {
  mSelected = false;
  mService: Service;
}

class ServiceInstanceUI {
  mSelected = false;
  mServiceInstance: ServiceInstance;
}
